import type { RgbColor } from '../color/types';
import type { Grid } from '../quantize/types';

/**
 * Mode de redimensionnement de l'image source vers la grille cible.
 *
 * - `nearest` : prend le pixel source le plus proche. Préserve les aplats nets
 *   (pixel art, logos). Sortie identique en taille mais avec aliasing visible.
 * - `bilinear` : moyenne pondérée des 4 pixels source voisins. Adoucit
 *   les transitions, mieux pour les photos.
 */
export type ResamplingMode = 'nearest' | 'bilinear';

/**
 * Redimensionne une grille source vers les dimensions cibles.
 *
 * @throws si les dimensions cibles sont invalides ou si la source est vide en dimensions positives.
 */
export function resize(
  source: Grid,
  targetWidth: number,
  targetHeight: number,
  mode: ResamplingMode,
): Grid {
  if (!Number.isInteger(targetWidth) || !Number.isInteger(targetHeight)) {
    throw new Error(`Dimensions cibles non entières : ${targetWidth}×${targetHeight}`);
  }
  if (targetWidth < 0 || targetHeight < 0) {
    throw new Error(`Dimensions cibles négatives : ${targetWidth}×${targetHeight}`);
  }
  if (targetWidth === 0 || targetHeight === 0) {
    return { width: targetWidth, height: targetHeight, pixels: [] };
  }
  if (source.width === 0 || source.height === 0) {
    throw new Error('Impossible de redimensionner depuis une source vide.');
  }

  return mode === 'nearest'
    ? resizeNearest(source, targetWidth, targetHeight)
    : resizeBilinear(source, targetWidth, targetHeight);
}

function resizeNearest(source: Grid, tw: number, th: number): Grid {
  const pixels: RgbColor[] = new Array<RgbColor>(tw * th);
  const sw = source.width;
  const sh = source.height;

  for (let y = 0; y < th; y++) {
    const srcY = Math.min(sh - 1, Math.floor((y * sh) / th));
    for (let x = 0; x < tw; x++) {
      const srcX = Math.min(sw - 1, Math.floor((x * sw) / tw));
      const p = source.pixels[srcY * sw + srcX];
      if (p === undefined) {
        throw new Error(`Pixel source manquant à (${srcX}, ${srcY}).`);
      }
      pixels[y * tw + x] = p;
    }
  }
  return { width: tw, height: th, pixels };
}

function resizeBilinear(source: Grid, tw: number, th: number): Grid {
  const pixels: RgbColor[] = new Array<RgbColor>(tw * th);
  const sw = source.width;
  const sh = source.height;

  for (let y = 0; y < th; y++) {
    // Centre du pixel cible dans le repère source (alignment "pixel centers").
    // Clamp-to-edge : les coordonnées source sont contraintes à [0, sw-1] / [0, sh-1].
    // Sans ce clamp, fx/fy peuvent devenir négatifs aux bordures et produire
    // des canaux < 0 par extrapolation linéaire.
    const syRaw = ((y + 0.5) * sh) / th - 0.5;
    const sy = Math.max(0, Math.min(sh - 1, syRaw));
    const y0 = Math.floor(sy);
    const y1 = Math.min(sh - 1, y0 + 1);
    const fy = sy - y0;

    for (let x = 0; x < tw; x++) {
      const sxRaw = ((x + 0.5) * sw) / tw - 0.5;
      const sx = Math.max(0, Math.min(sw - 1, sxRaw));
      const x0 = Math.floor(sx);
      const x1 = Math.min(sw - 1, x0 + 1);
      const fx = sx - x0;

      const p00 = source.pixels[y0 * sw + x0];
      const p10 = source.pixels[y0 * sw + x1];
      const p01 = source.pixels[y1 * sw + x0];
      const p11 = source.pixels[y1 * sw + x1];
      if (p00 === undefined || p10 === undefined || p01 === undefined || p11 === undefined) {
        throw new Error(`Pixel source manquant lors de l'interpolation à (${x}, ${y}).`);
      }

      const top0R = p00.r * (1 - fx) + p10.r * fx;
      const top0G = p00.g * (1 - fx) + p10.g * fx;
      const top0B = p00.b * (1 - fx) + p10.b * fx;
      const bot1R = p01.r * (1 - fx) + p11.r * fx;
      const bot1G = p01.g * (1 - fx) + p11.g * fx;
      const bot1B = p01.b * (1 - fx) + p11.b * fx;

      pixels[y * tw + x] = {
        r: Math.round(top0R * (1 - fy) + bot1R * fy),
        g: Math.round(top0G * (1 - fy) + bot1G * fy),
        b: Math.round(top0B * (1 - fy) + bot1B * fy),
      };
    }
  }
  return { width: tw, height: th, pixels };
}
